import Flex from 'components/common/Flex';
import TableRowAction from 'components/common/TableRowAction';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Card, Col, Row, Form, Image, InputGroup, FormControl, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';
import StrictModeDroppable from 'components/projects/kanban/StrictModeDroppable';
import IconButton from 'components/common/IconButton';
import paths from 'routes/paths';
import { parseRouteParams } from 'helpers/utils';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import ExportModal from 'components/common/ExportModal';
import { useBranches } from 'hooks/useBranches';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';
import { Can } from 'routes/permissions/Can';
import { userActions } from 'routes/permissions/permissionKeys';
import {
  useUrgentAppeals,
  useDeleteUrgentAppeal,
  useUpdateUrgentAppeal,
  useUpdateUrgentAppealOrder
} from 'hooks/useUrgentAppeals';

const UrgentAppeals = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderedAppeals, setOrderedAppeals] = useState([]);

  const { urgentAppeals, isLoading } = useUrgentAppeals();
  const { branches } = useBranches();
  const { deleteUrgentAppeal, isDeleting } = useDeleteUrgentAppeal();
  const { updateAppealOrder } = useUpdateUrgentAppealOrder();

  const navigate = useNavigate();

  useEffect(() => {
    const dataTmp = urgentAppeals?.map(itemAppeal => {
      const branch = branches?.find(item => item.id === itemAppeal.branch_id)
        ?.name;
      return { ...itemAppeal, branch };
    });
    setOrderedAppeals(dataTmp || []);
  }, [urgentAppeals, branches]);

  const filteredAppeals = useMemo(() => {
    if (!searchTerm) return orderedAppeals;
    const lower = searchTerm.toLowerCase();
    return orderedAppeals.filter(
      appeal =>
        appeal.title?.toLowerCase().includes(lower) ||
        appeal.branch?.toLowerCase().includes(lower)
    );
  }, [orderedAppeals, searchTerm]);

  const handleDragEnd = useCallback(
    async result => {
      if (!result.destination) return;
      if (result.destination.index === result.source.index) return;

      const reordered = Array.from(orderedAppeals);
      const [moved] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, moved);

      const previous = orderedAppeals;
      setOrderedAppeals(reordered);

      const updates = reordered.map((appeal, index) => ({
        id: appeal.id,
        display_order: index
      }));

      try {
        await updateAppealOrder(updates);
      } catch (error) {
        console.error(error);
        setOrderedAppeals(previous);
      }
    },
    [orderedAppeals, updateAppealOrder]
  );

  const handleDeleteClick = id => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteModalHide = () => {
    setSelectedId(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = () => {
    deleteUrgentAppeal(selectedId, {
      onSuccess: () => {
        handleDeleteModalHide();
      }
    });
  };

  const exportColumns = [
    { accessor: 'images', Header: 'Image' },
    { accessor: 'title', Header: 'Appeal Title' },
    { accessor: 'branch', Header: 'Branch' },
    { accessor: 'status', Header: 'Display' }
  ];

  // Drag-and-drop only works when not filtering
  const isDragDisabled = !!searchTerm;

  return (
    <>
      <ConfirmationModal
        show={showDeleteModal}
        onHide={handleDeleteModalHide}
        loading={isDeleting}
        title="Delete Urgent Appeal"
        handleConfirm={handleConfirmDelete}
        body="Are you sure you want to delete selected urgent appeal?"
      />
      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <Card className="border shadow-none">
            <Card.Header>
              <Flex
                justifyContent="between"
                alignItems="center"
                wrap="wrap"
                className="gap-2"
              >
                <h5 className="mb-0">Urgent Appeals</h5>
                <Flex alignItems="center" wrap="wrap" className="gap-2">
                  <InputGroup size="sm" style={{ width: 200 }}>
                    <FormControl
                      placeholder="Search"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                  <IconButton
                    variant="falcon-default"
                    size="sm"
                    icon="file-export"
                    onClick={() => setShowExportModal(true)}
                  >
                    <span className="d-none d-sm-inline-block ms-1">
                      Export
                    </span>
                  </IconButton>
                  <Can
                    I={userActions.create}
                    a={permissionsKeyMap.donation.children.urgentAppeals}
                  >
                    <IconButton
                      variant="falcon-default"
                      size="sm"
                      icon="plus"
                      onClick={() => navigate(paths.createUrgentAppeal)}
                    >
                      <span className="d-none d-sm-inline-block ms-1">
                        Create New Appeal
                      </span>
                    </IconButton>
                  </Can>
                </Flex>
              </Flex>
            </Card.Header>
            <Card.Body className="p-0">
              {isLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : filteredAppeals.length === 0 ? (
                <p className="text-muted text-center py-4 mb-0">
                  {searchTerm ? 'No matching appeals found.' : 'No urgent appeals found.'}
                </p>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <StrictModeDroppable droppableId="urgent-appeals-order">
                    {provided => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {filteredAppeals.map((appeal, index) => (
                          <Draggable
                            key={appeal.id}
                            draggableId={appeal.id}
                            index={index}
                            isDragDisabled={isDragDisabled}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`d-flex align-items-center px-3 py-2 border-bottom ${
                                  snapshot.isDragging
                                    ? 'shadow-sm bg-light'
                                    : 'bg-white'
                                }`}
                                style={{
                                  ...provided.draggableProps.style,
                                  cursor: isDragDisabled
                                    ? 'default'
                                    : 'grab'
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faGripVertical}
                                  className={`me-3 ${isDragDisabled ? 'text-300' : 'text-muted'}`}
                                />
                                <div className="me-3" style={{ width: 50, flexShrink: 0 }}>
                                  {appeal.images?.primary ? (
                                    <Image
                                      src={appeal.images.primary}
                                      alt="Appeal"
                                      rounded
                                      style={{
                                        width: 50,
                                        height: 30,
                                        objectFit: 'cover'
                                      }}
                                    />
                                  ) : (
                                    <span className="text-400">-</span>
                                  )}
                                </div>
                                <div className="flex-grow-1 me-3">
                                  <Button
                                    variant="link"
                                    onClick={() =>
                                      navigate(
                                        parseRouteParams(
                                          paths.editUrgentAppeal,
                                          { id: appeal.id }
                                        )
                                      )
                                    }
                                    className="fw-medium fs--1 p-0 text-1100 hover-primary"
                                  >
                                    {appeal.title}
                                  </Button>
                                </div>
                                <div
                                  className="text-muted fs--1 me-3 d-none d-md-block"
                                  style={{ width: 120, flexShrink: 0 }}
                                >
                                  {appeal.branch || '-'}
                                </div>
                                <div
                                  className="me-3"
                                  style={{ width: 50, flexShrink: 0 }}
                                >
                                  <DisplayToggle
                                    id={appeal.id}
                                    checked={appeal.is_display}
                                  />
                                </div>
                                <div style={{ flexShrink: 0 }}>
                                  <TableRowAction
                                    onCLickEdit={() =>
                                      navigate(
                                        parseRouteParams(
                                          paths.editUrgentAppeal,
                                          { id: appeal.id }
                                        )
                                      )
                                    }
                                    onCLickDelete={() =>
                                      handleDeleteClick(appeal.id)
                                    }
                                    subject={
                                      permissionsKeyMap.donation.children
                                        .urgentAppeals
                                    }
                                  />
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </StrictModeDroppable>
                </DragDropContext>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <ExportModal
        data={orderedAppeals || []}
        columns={exportColumns}
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        fileName={'Urgent Appeals'}
      />
    </>
  );
};

const DisplayToggle = ({ checked, id }) => {
  const { updateUrgentAppeal, isUpdating } = useUpdateUrgentAppeal();

  const handleChange = e => {
    updateUrgentAppeal({
      id,
      data: { is_display: e.target.checked }
    });
  };

  return (
    <Form.Check
      type="switch"
      defaultChecked={checked}
      checked={checked}
      value={checked}
      onChange={handleChange}
      disabled={isUpdating}
    />
  );
};

DisplayToggle.propTypes = {
  checked: PropTypes.bool,
  id: PropTypes.string
};

export default UrgentAppeals;
